import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { Loader2, ChevronLeft, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * OperatorWeeklyCard — this/last week's accounting for an operator.
 * Pure read-only summary; reconciliation happens admin-side.
 * Visual style: muted admin grey (matches OperatorDashboard).
 */
const OperatorWeeklyCard = () => {
    const [cycle, setCycle] = useState('current');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/operator/weekly`, { params: { cycle }, withCredentials: true });
            setData(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [cycle]);

    useEffect(() => { load(); }, [load]);

    if (loading && !data) {
        return (
            <div className="rounded-md border border-zinc-800 bg-zinc-900/30 p-6 text-center text-zinc-500 mb-6">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading week…
            </div>
        );
    }
    if (!data) return null;

    const t = data.totals;
    const settled = data.settlement?.settled;
    const start = new Date(data.cycle_start);
    const end = new Date(data.cycle_end);
    const opts = { month: 'short', day: 'numeric' };
    const range = `${start.toLocaleDateString('en-US', opts)} → ${end.toLocaleDateString('en-US', opts)} (CT)`;
    const houseNeg = t.net_house < 0;

    return (
        <div className="rounded-md border border-zinc-800 bg-zinc-900/30 mb-6" data-testid="operator-weekly-card">
            <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-zinc-800">
                <div>
                    <div className="flex items-center gap-2">
                        <h2 className="text-sm font-semibold text-zinc-100">Weekly settlement</h2>
                        {settled && (
                            <span className="text-[10px] uppercase tracking-widest text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 px-2 py-0.5 rounded inline-flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" /> Settled
                            </span>
                        )}
                    </div>
                    <p className="text-xs text-zinc-500 mt-0.5">{data.cycle} · {range}</p>
                </div>
                <Tabs value={cycle} onValueChange={setCycle}>
                    <TabsList className="bg-zinc-950 border border-zinc-800 h-8">
                        <TabsTrigger value="prev" className="text-xs data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-100" data-testid="op-cycle-prev">
                            <ChevronLeft className="w-3 h-3 mr-1" />Last week
                        </TabsTrigger>
                        <TabsTrigger value="current" className="text-xs data-[state=active]:bg-zinc-800 data-[state=active]:text-zinc-100" data-testid="op-cycle-current">
                            This week
                        </TabsTrigger>
                    </TabsList>
                </Tabs>
            </div>

            <div className="p-5 space-y-4">
                {/* Activity totals — neutral tiles, value colour reserved for net house */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Cell label="Loaded" value={fmt(t.loaded)} sub={`${t.load_count} txns`} />
                    <Cell label="Cashed out" value={fmt(t.cashed)} sub={`${t.cashout_count} txns`} />
                    <Cell label="Wagered" value={fmt(t.wagered)} />
                    <Cell label="Won" value={fmt(t.won)} />
                </div>

                {/* Net house — the one number with chromatic emphasis */}
                <div className={`rounded-md border px-5 py-4 ${houseNeg ? 'border-rose-900/50 bg-rose-950/20' : 'border-emerald-900/50 bg-emerald-950/20'}`}>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium">Net house (this week)</p>
                    <p className={`text-3xl font-mono font-semibold mt-1 ${houseNeg ? 'text-rose-300' : 'text-emerald-300'}`} data-testid="op-net-house">
                        {fmt(t.net_house)}
                    </p>
                    <p className="text-xs text-zinc-500 mt-1">Loaded − Cashed out. Settled at week-end with the agreed split.</p>
                </div>

                {/* Per-cashier breakdown */}
                {data.by_cashier?.length > 0 && (
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium mb-2">By cashier</p>
                        <div className="space-y-1">
                            {data.by_cashier.map((c) => (
                                <div key={c.cashier_id || 'direct'} className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm">
                                    <span className="text-zinc-200 font-medium">{c.cashier_name}</span>
                                    <span className="font-mono text-zinc-400 text-xs">
                                        <span className="text-zinc-200">{fmt(c.loaded)}</span> loaded · <span className="text-zinc-200">{fmt(c.cashed)}</span> cashed
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const Cell = ({ label, value, sub }) => (
    <div className="rounded-md border border-zinc-800 bg-zinc-950 px-4 py-3">
        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-medium">{label}</p>
        <p className="text-xl font-mono font-semibold text-zinc-100 mt-1">{value}</p>
        {sub && <p className="text-[10px] text-zinc-500 mt-0.5">{sub}</p>}
    </div>
);

export default OperatorWeeklyCard;
